import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';
import { TASK_STATUS } from 'constants/interfaces';

import type { TTaskStatus } from 'constants/interfaces';
import type IStatusTaskButton from './interfaces/IStatusTaskButton';

import styles from './scss/StatusTaskButton.module.scss';

function StatusTaskButton({
  taskId,
  currentStatus,
  onStatusChange,
  className,
  disabled = false,
}: IStatusTaskButton) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const STATUS_OPTIONS: TTaskStatus[] = [
    TASK_STATUS.created,
    TASK_STATUS.assigned,
    TASK_STATUS.inProgress,
    TASK_STATUS.completed,
    TASK_STATUS.deferred,
    TASK_STATUS.blocked,
    TASK_STATUS.cancelled,
  ];

  const getButtonClassName = (status: TTaskStatus): string => {
    const statusMap: Record<TTaskStatus, string> = {
      [TASK_STATUS.created]: styles.buttonCreated,
      [TASK_STATUS.assigned]: styles.buttonAssigned,
      [TASK_STATUS.inProgress]: styles.buttonInProgress,
      [TASK_STATUS.completed]: styles.buttonCompleted,
      [TASK_STATUS.deferred]: styles.buttonDeferred,
      [TASK_STATUS.blocked]: styles.buttonBlocked,
      [TASK_STATUS.cancelled]: styles.buttonCancelled,
    };
    return statusMap[status] || styles.buttonCreated;
  };

  const getDropdownItemClassName = (status: TTaskStatus): string => {
    const statusMap: Record<TTaskStatus, string> = {
      [TASK_STATUS.created]: styles.dropdownItemCreated,
      [TASK_STATUS.assigned]: styles.dropdownItemAssigned,
      [TASK_STATUS.inProgress]: styles.dropdownItemInProgress,
      [TASK_STATUS.completed]: styles.dropdownItemCompleted,
      [TASK_STATUS.deferred]: styles.dropdownItemDeferred,
      [TASK_STATUS.blocked]: styles.dropdownItemBlocked,
      [TASK_STATUS.cancelled]: styles.dropdownItemCancelled,
    };
    return statusMap[status] || styles.dropdownItemCreated;
  };

  const checkDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = STATUS_OPTIONS.length * 40 + 16;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    setOpenUpward(spaceBelow < dropdownHeight && spaceAbove >= dropdownHeight);
  }, [STATUS_OPTIONS.length]);

  const handleToggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        checkDropdownPosition();
      }
      setIsOpen(!isOpen);
    }
  };

  const handleStatusSelect = (newStatus: TTaskStatus) => {
    if (newStatus !== currentStatus) {
      onStatusChange(taskId, newStatus);
    }
    setIsOpen(false);
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', checkDropdownPosition);
      window.addEventListener('resize', checkDropdownPosition);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', checkDropdownPosition);
      window.removeEventListener('resize', checkDropdownPosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', checkDropdownPosition);
      window.removeEventListener('resize', checkDropdownPosition);
    };
  }, [isOpen, checkDropdownPosition, handleClickOutside]);

  return (
    <div className={clsx(styles.root, className)} ref={dropdownRef}>
      <button
        type="button"
        ref={buttonRef}
        className={clsx(styles.button, getButtonClassName(currentStatus))}
        onClick={handleToggleDropdown}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        tabIndex={0}
      >
        <span className={styles.buttonText}>{t(`taskManagement.statusOptions.${currentStatus}`)}</span>
        <ChevronIcon
          className={clsx(styles.chevron, {
            [styles.chevronRotated]: isOpen,
          })}
        />
      </button>

      <div
        className={clsx(styles.dropdown, {
          [styles.dropdownOpen]: isOpen,
          [styles.dropdownUpward]: openUpward,
        })}
        role="listbox"
        aria-label={t('taskManagement.status')}
      >
        {STATUS_OPTIONS.map((status) => (
          <div
            key={status}
            className={clsx(
              styles.dropdownItem,
              getDropdownItemClassName(status),
              {
                [styles.selectedItem]: status === currentStatus,
              },
            )}
            onClick={() => handleStatusSelect(status)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStatusSelect(status);
              }
            }}
            role="option"
            aria-selected={status === currentStatus}
            tabIndex={0}
          >
            <span>{t(`taskManagement.statusOptions.${status}`)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default StatusTaskButton;
